import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { PaneControls } from '../../components/Panes';
import { Layers3, ListEnd, ListPlus, Music, Replace } from 'lucide-react';
import { TICKS_PER_SECOND, formatDuration, type SourceRange } from '@prism/contracts';
import type { SourceMarks } from './threePointEdit';
import { useAsset, useSubtitles } from '../../api/hooks';
import { Player, type PlayerHandle } from '../../player/Player';

/** Stills have no length of their own; on a layer they start at this length and can be stretched. */
const STILL_TICKS = 5 * TICKS_PER_SECOND;

/** What the editor reads from the source viewer at the moment of an edit (1.7.4: Insert, Overwrite, the , and . keys). */
export interface SourceViewerHandle {
  /** The loaded source with its marks as set, or null when nothing (or nothing editable) is loaded. */
  marks(): SourceMarks | null;
}

/** Match frame (1.7.4): open this asset at this source time with this In and Out; `n` makes a repeat a new cue. */
export interface SourceCue { assetId: string; sourceTicks: number; inTicks: number; outTicks: number; n: number }

export function SourceViewer({ assetId, active, onActivate, onAppend, onInsert, onOverwrite, onAddAudio, onAddLayer, canEdit, handle, cue }: {
  assetId: string | null;
  active: boolean;
  onActivate: () => void;
  onAppend: (r: SourceRange) => void;
  /** 1.7.4: three-point Insert and Overwrite on the targeted tracks (the editor reads the marks through `handle`). */
  onInsert: () => void;
  onOverwrite: () => void;
  onAddAudio: (r: SourceRange) => void;
  onAddLayer: (r: SourceRange) => void;
  canEdit: boolean;
  handle?: Ref<SourceViewerHandle>;
  cue?: SourceCue | null;
}) {
  const asset = useAsset(assetId);
  const subs = useSubtitles(assetId ?? undefined);
  const [rangeState, setRange] = useState<{ in: number | null; out: number | null }>({ in: null, out: null });
  const onRange = useCallback((r: { in: number | null; out: number | null }) => setRange(r), []);
  const player = useRef<PlayerHandle>(null);
  const a = asset.data;
  // A recording (1.0.0-rc.1): plays what is recorded so far, and In and Out are marked within it.
  const live = a && !a.media.proxyUrl && a.live?.snapshotUrl && (a.live.state === 'recording' || a.live.state === 'finishing') ? a.live : null;
  const durationTicks = a?.durationTicks ?? live?.recordedTicks ?? 0;
  const mediaKind = a?.mediaKind ?? (live ? 'video' : null);
  const still = mediaKind === 'image';
  /** The marks in seconds, as the player shows them (match frame sets them through `setRange`). */
  function marksNow(live: boolean): { in: number | null; out: number | null } {
    return live && player.current ? player.current.range() : rangeState;
  }
  const src = (live = false): SourceRange | null => {
    if (!a || !a.versionId) return null;
    // The picture's displayed size (1.0.0-rc.11): a clip of the other orientation than the sequence is placed with Fill.
    // 1.8.0: whether it has sound (its probe's audio stream), so a silent clip on a video track gets no audio clip.
    const size = { width: a.width, height: a.height, ...(a.technical ? { hasAudio: a.technical.streams.some((st) => st.type === 'audio') } : {}) };
    if (still) return { assetId: a.id, versionId: a.versionId, label: a.title, sourceInTicks: 0, sourceOutTicks: STILL_TICKS, sourceDurationTicks: 0, ...size };
    const range = marksNow(live);
    const inT = range.in != null ? Math.round(range.in * TICKS_PER_SECOND) : 0;
    const outT = range.out != null ? Math.round(range.out * TICKS_PER_SECOND) : durationTicks;
    return { assetId: a.id, versionId: a.versionId, label: a.title, sourceInTicks: inT, sourceOutTicks: Math.min(outT, durationTicks), sourceDurationTicks: durationTicks, ...size };
  };
  const r = src();
  useImperativeHandle(handle, () => ({
    marks: () => {
      const range = src(true);
      if (!range || !a) return null;
      const m = marksNow(true);
      const kind = mediaKind === 'video' || mediaKind === 'audio' || mediaKind === 'image' ? mediaKind : null;
      return { range, sourceIn: still || m.in == null ? null : Math.round(m.in * TICKS_PER_SECOND), sourceOut: still || m.out == null ? null : Math.round(m.out * TICKS_PER_SECOND), duration: still ? 0 : durationTicks, kind };
    },
  }));
  // Match frame: once the asset is loaded, its frame and the clip's In and Out.
  const cued = useRef(0);
  useEffect(() => {
    if (!cue || cue.n === cued.current || !a || a.id !== cue.assetId) return;
    cued.current = cue.n;
    const inSec = cue.inTicks / TICKS_PER_SECOND;
    const outSec = cue.outTicks / TICKS_PER_SECOND;
    const at = cue.sourceTicks / TICKS_PER_SECOND;
    let tries = 0;
    const go = () => {
      const el = player.current?.element();
      if (!el && tries++ < 40) { window.setTimeout(go, 100); return; }
      // The clip's In and Out become the player's own marks, shown on its timeline and replaced by I and O.
      player.current?.setRange(inSec, outSec);
      player.current?.seek(at);
      if (el && el.readyState < 1) el.addEventListener('loadedmetadata', () => player.current?.seek(at), { once: true });
    };
    go();
  }, [cue, a]);
  return (
    <div className={`viewer ${active ? 'active' : ''}`} onPointerDown={onActivate} data-testid="source-viewer">
      <div className="viewer-head">
        <span className="pane-title">Source</span>
        {a && <span className="viewer-title">{a.title}</span>}
        <span style={{ flex: 1 }} />
        {r && <span className="faint mono" style={{ fontSize: 12 }}>{formatDuration(r.sourceOutTicks - r.sourceInTicks)} selected</span>}
        <PaneControls />
      </div>
      {a && (a.media.proxyUrl || live) && !still ? (
        <div className="viewer-player">
          <Player ref={player} asset={a} compact keyboard={active} onRangeChange={onRange} subtitleTracks={subs.data?.items ?? []}
            live={live ? { url: live.snapshotUrl!, followUrl: live.followUrl ?? null, recordedSeconds: (live.recordedTicks ?? 0) / TICKS_PER_SECOND, state: live.state } : undefined} />
        </div>
      ) : (
        // A still, or nothing picked yet: the same rows as the player (picture, transport), so the source and
        // sequence pictures and their rows line up (0.96.0).
        <>
          <div className="viewer-picture">
            {a?.media.proxyUrl
              ? <div className="viewer-stage checkerboard"><img src={a.media.proxyUrl} alt={a.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} /></div>
              : <div className="viewer-stage"><div className="viewer-empty">{assetId ? 'Loading…' : 'Pick media from the bin. Mark in (I) and out (O), then add it to the sequence.'}</div></div>}
          </div>
          <div className="transport viewer-transport" aria-hidden><div className="timeline placeholder" /><div className="controls" /></div>
        </>
      )}
      {/* The edit buttons; an empty row for people who may not edit, so both viewers keep the same rows. */}
      <div className="viewer-controls" data-testid="source-buttons">
        {canEdit && (
          <>
            <button className="btn sm" disabled={!r} onClick={onInsert} data-testid="insert-clip"
              title="Insert (,): at the playhead or the sequence In, on the tracks targeted with Source V and Source A; what is after it moves later"><ListPlus size={14} /> Insert</button>
            <button className="btn sm" disabled={!r} onClick={onOverwrite} data-testid="overwrite-clip"
              title="Overwrite (.): at the playhead or the sequence In, over what is on the targeted tracks; nothing moves"><Replace size={14} /> Overwrite</button>
            <button className="btn sm primary" disabled={!r || mediaKind !== 'video'} onClick={() => { const x = src(true); if (x) onAppend(x); }} data-testid="append-clip" title="Append to the end of the storyline"><ListEnd size={14} /> Append</button>
            <button className="btn sm" disabled={!r || mediaKind === 'audio'} onClick={() => { const x = src(true); if (x) onAddLayer(x); }} data-testid="add-layer-clip" title="Put it on a video track above V1 at the playhead (picture in picture, overlays, alpha-channel media)"><Layers3 size={14} /> Add to track</button>
            <button className="btn sm" disabled={!r || still} onClick={() => { const x = src(true); if (x) onAddAudio(x); }} data-testid="add-audio" title="Add to the second audio track at the playhead"><Music size={14} /> Add to A2</button>
          </>
        )}
      </div>
    </div>
  );
}
